(() => {
  'use strict';
  if (window.BasuriNative) { window.BasuriNative.init(document); return; }
  const config = window.BasuriSettings;
  const root = window.Shopify?.routes?.root || config.root || '/';
  const drawer = document.getElementById('cart-drawer');
  const initialized = new WeakSet();
  let busy = false, opener = null, closeTimer, variantRequest;
  const parse = html => new DOMParser().parseFromString(html, 'text/html');
  function message(text) { document.querySelectorAll('[data-native-message]').forEach(el => { el.textContent = text; }); }
  function init(scope = document) {
    scope.querySelectorAll('[data-variant-id]').forEach(field => { field.disabled = false; });
    scope.querySelectorAll('[data-recommendations]').forEach(el => {
      if (initialized.has(el)) return;
      initialized.add(el);
      fetch(el.dataset.url).then(response => { if (!response.ok) throw new Error(); return response.text(); }).then(html => {
        const content = parse(html).querySelector('[data-recommendations]');
        if (content && el.isConnected) el.innerHTML = content.innerHTML;
      }).catch(() => { /* Recommendations are optional and do not block shopping. */ });
    });
  }
  function optionsClose(scope = drawer) {
    scope?.querySelectorAll('[data-cart-option]').forEach(button => button.setAttribute('aria-expanded', 'false'));
    scope?.querySelectorAll('[data-option-panel]').forEach(panel => panel.classList.remove('is-open'));
  }
  function openDrawer(trigger) {
    if (!drawer) { location.assign(config.cart); return; }
    clearTimeout(closeTimer); drawer.classList.remove('is-closing');
    window.BasuriUI?.closeMenu();
    document.querySelectorAll('audio').forEach(audio => audio.pause());
    opener = trigger;
    if (!drawer.open) drawer.showModal();
    document.body.classList.add('cart-is-open');
    document.querySelectorAll('[data-cart-open]').forEach(link => link.setAttribute('aria-expanded', 'true'));
    drawer.querySelector('[data-native-close]')?.focus();
  }
  function closeDrawer() {
    if (!drawer?.open) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { drawer.close(); return; }
    drawer.classList.add('is-closing'); clearTimeout(closeTimer);
    closeTimer = setTimeout(() => drawer.close(), 180);
  }
  drawer?.addEventListener('cancel', event => { event.preventDefault(); closeDrawer(); });
  drawer?.addEventListener('close', () => {
    clearTimeout(closeTimer); drawer.classList.remove('is-closing'); optionsClose();
    document.body.classList.remove('cart-is-open');
    document.querySelectorAll('[data-cart-open]').forEach(link => link.setAttribute('aria-expanded', 'false'));
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  });
  drawer?.addEventListener('click', event => {
    if (event.target !== drawer) return;
    const rect = drawer.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDrawer();
  });
  function lock(value) {
    busy = value;
    document.querySelectorAll('[data-cart-region]').forEach(el => el.setAttribute('aria-busy', String(value)));
    document.querySelectorAll('[data-native-cart-form] button,[data-native-cart-form] input,[data-native-cart-form] textarea,.native-product-form button[name=add]').forEach(el => {
      if (value) { el.dataset.wasDisabled = String(el.disabled); el.disabled = true; }
      else if ('wasDisabled' in el.dataset) { el.disabled = el.dataset.wasDisabled === 'true'; delete el.dataset.wasDisabled; }
    });
  }
  async function renderCart(data) {
    let html = data?.sections?.['cart-render'];
    if (!html) {
      const response = await fetch(`${root}?section_id=cart-render`, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error(config.strings.error);
      html = await response.text();
    }
    const doc = parse(html), count = doc.querySelector('[data-render-count]');
    if (!count || !doc.querySelector('[data-cart-fragment=drawer]')) throw new Error(config.strings.error);
    const activeId = document.activeElement?.id;
    document.querySelectorAll('[data-cart-region]').forEach(region => {
      const fragment = doc.querySelector(`[data-cart-fragment="${region.dataset.cartRegion}"]`);
      if (fragment) region.innerHTML = fragment.innerHTML;
    });
    document.querySelectorAll('[data-native-count]').forEach(el => { el.textContent = count.dataset.renderCount; el.hidden = Number(count.dataset.renderCount) === 0; });
    if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
  }
  async function mutate(endpoint, payload, success) {
    if (busy) return null;
    lock(true); message('');
    try {
      let body, headers;
      if (payload instanceof FormData) {
        payload.set('sections', 'cart-render'); payload.set('sections_url', location.pathname);
        body = payload;
      } else { body = JSON.stringify({ ...payload, sections: ['cart-render'], sections_url: location.pathname }); headers = { 'Content-Type': 'application/json' }; }
      const response = await fetch(root + endpoint, { method: 'POST', headers, body, credentials: 'same-origin' });
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : config.strings.error);
      try { await renderCart(data); } catch { location.assign(config.cart); return null; }
      message(success);
      return data;
    } catch (error) {
      message(error.message || config.strings.error);
      try { await renderCart(); } catch { /* Keep the native cart link and retry controls usable. */ }
      throw error;
    } finally { lock(false); }
  }
  function normalized(field, direction = 0) {
    const min = Number(field.min) || 1, step = Number(field.step) || 1;
    const max = field.max ? Number(field.max) : Infinity;
    const value = min + Math.round(((Number(field.value) || min) - min) / step) * step + direction * step;
    return Math.max(min, Math.min(max, value));
  }
  async function changeQuantity(field) {
    if (busy) return;
    field.value = String(normalized(field));
    await mutate('cart/change.js', { id: field.dataset.nativeQuantity, quantity: Number(field.value) }, config.strings.updated).catch(() => {});
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('button,a');
    if (!button) return;
    if (button.matches('[data-cart-open]')) {
      if (!config.drawer || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); openDrawer(button);
      if (!busy) { lock(true); renderCart().catch(() => message(config.strings.error)).finally(() => lock(false)); }
    } else if (button.matches('[data-native-close]')) closeDrawer();
    else if (button.matches('[data-native-step]')) {
      if (busy) return;
      const field = button.parentElement.querySelector('input[type=number]');
      field.value = String(normalized(field, Number(button.dataset.nativeStep)));
      if (field.matches('[data-native-quantity]')) changeQuantity(field);
    } else if (button.matches('[data-native-remove]')) {
      event.preventDefault();
      mutate('cart/change.js', { id: button.dataset.nativeRemove, quantity: 0 }, config.strings.updated).catch(() => {});
    } else if (button.matches('[data-cart-option]')) {
      const form = button.closest('form'), open = button.getAttribute('aria-expanded') !== 'true';
      optionsClose(form);
      if (open) { button.setAttribute('aria-expanded', 'true'); const panel = document.getElementById(button.getAttribute('aria-controls')); panel.classList.add('is-open'); panel.querySelector('input,textarea')?.focus({ preventScroll: true }); }
    } else if (button.matches('[data-native-cancel]')) {
      const form = button.closest('form'), type = button.dataset.nativeCancel;
      const field = form.querySelector(type === 'note' ? '[name=note]' : '[data-discount-input]');
      field.value = field.defaultValue; optionsClose(form); form.querySelector(`[data-cart-option="${type}"]`)?.focus();
    } else if (button.matches('[data-native-save]')) {
      const form = button.closest('form'), type = button.dataset.nativeSave;
      const value = form.querySelector(type === 'note' ? '[name=note]' : '[data-discount-input]').value.trim();
      mutate('cart/update.js', type === 'note' ? { note: value } : { discount: value }, type === 'note' ? config.strings.note : config.strings.discount).then(data => {
        if (type === 'coupon' && value && data?.discount_codes?.some(code => code.applicable === false)) message(config.strings.discountError);
      }).catch(() => {});
    }
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!form.matches('.native-product-form')) return;
    event.preventDefault(); if (busy) return;
    const payload = new FormData(form), trigger = event.submitter || form.querySelector('[name=add]');
    const status = form.querySelector('[data-product-message]'); status.textContent = '';
    try {
      const data = await mutate('cart/add.js', payload, config.strings.added);
      if (data) { status.textContent = config.strings.added; if (config.drawer) openDrawer(trigger); else location.assign(config.cart); }
    } catch (error) { status.textContent = error.message; }
  });
  async function updateVariant(select) {
    const section = select.closest('[data-product-section]');
    if (!section) return;
    variantRequest?.abort(); const controller = new AbortController(); variantRequest = controller;
    const selected = select.selectedOptions[0];
    const url = new URL(selected?.dataset.productUrl || section.dataset.productUrl, location.origin);
    url.searchParams.set('option_values', [...section.querySelectorAll('[data-option-select]')].map(el => el.value).join(','));
    const plan = section.querySelector('[data-selling-plan]')?.value;
    if (plan) url.searchParams.set('selling_plan', plan);
    if (url.pathname !== new URL(section.dataset.productUrl, location.origin).pathname) { location.assign(url); return; }
    url.searchParams.set('section_id', section.dataset.sectionId);
    const focusId = select.id;
    section.setAttribute('aria-busy', 'true');
    const add = section.querySelector('[name=add]'); if (add) add.disabled = true;
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error();
      const replacement = parse(await response.text()).querySelector('[data-product-section]');
      if (!replacement) throw new Error();
      section.dispatchEvent(new CustomEvent('shopify:section:unload', { bubbles: true }));
      section.replaceWith(replacement);
      init(replacement); window.BasuriCommerce?.init(replacement);
      document.getElementById(focusId)?.focus({ preventScroll: true });
      url.searchParams.delete('section_id'); history.replaceState({}, '', url);
      window.Shopify?.PaymentButton?.init();
    } catch (error) {
      if (error.name !== 'AbortError') { url.searchParams.delete('section_id'); location.assign(url); }
    }
  }
  document.addEventListener('change', event => {
    if (event.target.matches('[data-native-sort]')) event.target.form.requestSubmit();
    if (event.target.matches('[data-native-quantity]')) changeQuantity(event.target);
    if (event.target.matches('[data-option-select],[data-selling-plan]')) updateVariant(event.target);
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Enter') return;
    if (event.target.matches('[data-discount-input]')) {
      event.preventDefault(); event.target.closest('form').querySelector('[data-native-save=coupon]')?.click();
    } else if (event.target.matches('[data-native-quantity]')) {
      event.preventDefault(); changeQuantity(event.target);
    }
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.addEventListener('pageshow', event => { if (event.persisted) { lock(false); renderCart().catch(() => {}); } });
  window.BasuriNative = { init };
  init();
})();
